import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import { Table } from './common/Table';
import {Loading} from './common/Loading';
import { Plus } from 'lucide-react';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const OrdersList = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orderStatus, setOrderStatus] = useState("all");
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("");

  const columns = [
    { key: 'formattedOrderNumber', label: 'Order ID', sortable: true },
    {
      key: 'customer',
      label: 'Customer',
      sortable: false,
      render: (value, row) => {
        const firstName = row.customer?.firstname || '';
        const lastName = row.customer?.lastname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName ? (
          <span className="font-medium text-gray-900">{fullName}</span>
        ) : <span className="text-gray-400">-</span>;
      }
    },
    { key: 'startDate', label: 'Start Date', sortable: false },
    { key: 'endDate', label: 'End Date', sortable: false },
    { key: 'orderTotal', label: 'Total ($)', sortable: true, render: (value) => (
      <span className="font-semibold text-gray-900">${parseFloat(value).toFixed(2)}</span>
    )},
    { 
      key: 'status', 
      label: 'Status', 
      sortable: true,
      editable: true, 
      editType: 'select',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'completed', label: 'Completed' }
      ],
      render: (value) => {
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          confirmed: 'bg-blue-100 text-blue-800',
          delivered: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800',
          completed: 'bg-purple-100 text-purple-800'
        };
        return (
          <span className={`px-2 py-1 rounded text-xs ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row, { isEditing, handleEdit, handleSave, handleCancel }) => {
        if (isEditing) {
          return (
            <div className="flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleSave(row); }}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                Save
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleCancel(row); }}
                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          );
        }
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Edit Status
          </button>
        );
      }
    }
  ];

  useEffect(() => {
    if (!token) {
      logout().then(() => navigate('/login', { replace: true }));
    }
  }, []);

  // Helper to flatten row for search
  const flattenRow = (row) => ({
    ...row,
    customer: `${row.customer?.firstname || ''} ${row.customer?.lastname || ''}`.trim()
  });
const fetchOrders = async (filters = {}) => {
  if (!token) return;
  try {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.orderStatus && filters.orderStatus !== 'all') {
      params.append('orderStatus', filters.orderStatus); // ✅ Changed 'status' to 'orderStatus'
    }
  
    const { data } = await axios.get(`${apiUrl}/order?${params.toString()}`, {
      headers: { "x-auth-token": token },
    });

setOrders(Array.isArray(data) ? data : []);
    const ordersWithSearch = data.map(order => {
      const itemsTotal =
        order.items?.reduce(
          (sum, item) =>
            sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
          0
        ) || 0;
      const invoiceTotal = (order.invoices || []).reduce(
        (sum, inv) => sum + (parseFloat(inv.total_amount) || 0),
        0
      );
      const total = invoiceTotal > 0 ? invoiceTotal : itemsTotal;

      return {
        ...order,
        customerName: `${order.customer?.firstname || ''} ${order.customer?.lastname || ''}`.trim(),
        orderTotal: total.toFixed(2)
      };
    });

    setOrders(ordersWithSearch);
  } catch (err) {
    console.error("Error fetching orders:", err);
    if (err.response?.status === 401) {
      await logout();
      navigate('/login', { replace: true });
    }
  } finally {
    setLoading(false);
  }
};

  const fetchFilters = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${apiUrl}/filter`, { headers: { "x-auth-token": token } });
      setSavedFilters(res.data);
    } catch (err) {
      console.error("Error fetching filters:", err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchOrders();
      fetchFilters();
    }
  }, []);

  const handleSaveOrderStatus = async (editedRow) => {
    try {
      await axios.put(`${apiUrl}/order/${editedRow.uuid}`, {
        status: editedRow.status
      }, {
        headers: { "x-auth-token": token },
      });
      
      // Update local state safely
      setOrders(prev => 
        prev.map(order => 
          order.uuid === editedRow.uuid
            ? { ...order, status: editedRow.status } // keep customer and other fields intact
            : order
        )
      );
    } catch (err) {
      console.error("Error updating order status:", err);
      alert('Failed to update order status');
    }
  };

  const handleSelectFilter = (filterUuid) => {
    setSelectedFilter(filterUuid);
    const filter = savedFilters.find(f => f.uuid === filterUuid);
    if (!filter) return;
    const { startDate, endDate, orderStatus } = filter.criteria || {};
    setStartDate(startDate || "");
    setEndDate(endDate || "");
    setOrderStatus(orderStatus || "all");
    fetchOrders({ startDate, endDate, orderStatus });
  };

  const handleSaveFilter = async () => {
    const name = prompt("Enter filter name:");
    if (!name) return;
    try {
      const res = await axios.post(`${apiUrl}/filter`, { name, criteria: { startDate, endDate, orderStatus } }, { headers: { "x-auth-token": token } });
      setSavedFilters([...savedFilters, res.data]);
      setSelectedFilter(res.data.uuid);
    } catch (err) {
      console.error("Error saving filter:", err);
    }
  };

  const handleEditFilter = async (uuid) => {
    const filter = savedFilters.find(f => f.uuid === uuid);
    if (!filter) return;
    const name = prompt("Edit filter name:", filter.name);
    if (!name) return;
    try {
      const res = await axios.put(`${apiUrl}/filter/${uuid}`, { name, criteria: { startDate, endDate, orderStatus } }, { headers: { "x-auth-token": token } });
      setSavedFilters(savedFilters.map(f => f.uuid === uuid ? res.data : f));
    } catch (err) {
      console.error("Error editing filter:", err);
    }
  };

  const handleDeleteFilter = async (uuid) => {
    if (!window.confirm('Are you sure you want to delete this filter?')) return;
    try {
      await axios.delete(`${apiUrl}/filter/${uuid}`, { headers: { "x-auth-token": token } });
      setSavedFilters(savedFilters.filter(f => f.uuid !== uuid));
      setSelectedFilter("");
    } catch (err) {
      console.error("Error deleting filter:", err);
    }
  };

 const handleNewOrder = () => {
      navigate('/orders/new');
  };
  const handleClick = (order) => navigate('/orders/' + order.uuid);
  const handleFilterSubmit = (e) => { 
    e.preventDefault(); 
    setSelectedFilter(""); 
    fetchOrders({ startDate, endDate, orderStatus }); 
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
    </div>
  );

  if(loading){
    return <Loading/>;
  }

  return (
    <div className="grid p-4 grid-cols-1 shadow-lg rounded md:rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">All Orders</h2>
            <div className="p-4">
          <button 
              className='btn-primary flex items-center gap-2 whitespace-nowrap'
              onClick={handleNewOrder}
          >
              <Plus size={20}/> 
              New Order
          </button>
      </div>
      {/* Saved Filters */}
      <div className="flex flex-row bg-gray-50 p-4 rounded-lg m-3 items-center">
        <h3 className="text-lg font-semibold mb-3 text-gray-700">Saved Filters &nbsp;</h3>
        <div className="flex flex-col sm:flex-row items-start sm:items-center">
          <select value={selectedFilter} onChange={(e) => handleSelectFilter(e.target.value)} className="w-full sm:w-auto p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
            <option value="">-- Select Saved Filter --</option>
            {savedFilters.map(f => <option key={f.uuid} value={f.uuid}>{f.name}</option>)}
          </select>&nbsp;
          <div className="flex gap-2 w-full sm:w-auto">
            <button onClick={handleSaveFilter} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium p-2 rounded-md transition duration-200 text-sm"> &nbsp;Save Current</button>
            {selectedFilter && <>
              <button onClick={() => handleEditFilter(selectedFilter)} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-3 rounded-md transition duration-200 text-sm">Edit</button>
              <button onClick={() => handleDeleteFilter(selectedFilter)} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-3 rounded-md transition duration-200 text-sm">Delete</button>
            </>}
          </div>
        </div>
      </div>

      {/* Filter Form */}
      <div className="bg-gray-50 rounded-lg mb-3">
        <h3 className="text-lg font-semibold mb-3 text-gray-700">Filter Orders</h3>
        <form onSubmit={handleFilterSubmit} className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status:</label>
            <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200">Apply Filters</button>
          </div>
        </form>
      </div>

      {/* Orders List */}
      {orders.length  === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">No orders found matching your criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={orders}
            onRowClick={handleClick}
            onSave={handleSaveOrderStatus}
            rowKey="uuid"
            searchable={true}
            searchPlaceholder="Search orders by ID, customer, or status..."
            paginated={true}
            pageSize={10}
            striped={true}
            hoverable={true}
          />
        </div>
      )}
    </div>
  );
};

export default OrdersList;